import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { PurchaseOrderMainProductionDeclarationStatusBadge } from '@/features/purchase-order/components/purchase-order-production-declaration-status-badge';
import { PurchaseOrderStatusBadge } from '@/features/purchase-order/components/purchase-order-status-badge';
import { useSupplierOptions } from '@/hooks/supplier/use-supplier-options';
import { formatDate } from '@/lib/format';
import type { Lotsizing } from '@/types/core/purchase-order/lot-sizing';
import type { ProductionDeclarationStatusCode } from '@/types/core/purchase-order/production-declaration-status-code';
import type {
  PurchaseOrder,
  PurchaseOrderStatusCode,
} from '@/types/core/purchase-order/purchase-order';
import type { Supplier } from '@/types/core/purchase-planning/supplier';
import { SupplierHoverCard } from '../../shared/supplier-hover-card';

export function usePurchaseOrdersTableColumns(): ColumnDef<PurchaseOrder>[] {
  const { t, i18n } = useTranslation(['purchaseOrders', 'purchaseOrder']);
  const locale = i18n.language;
  const { supplierOptions } = useSupplierOptions();
  const declarationOptions = useMemo(
    () => [
      {
        label: t('productionDeclarationStatus.pending', {
          ns: 'purchaseOrder',
        }),
        value: 'pending',
      },
      {
        label: t('productionDeclarationStatus.submitted', {
          ns: 'purchaseOrder',
        }),
        value: 'submitted',
      },
      {
        label: t('productionDeclarationStatus.approved', {
          ns: 'purchaseOrder',
        }),
        value: 'approved',
      },
    ],
    [t],
  );

  return useMemo(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('name')} />
        ),
        cell: ({ row }) => <div>{row.renderValue('name')}</div>,
        enableHiding: false,
        enableColumnFilter: true,
        meta: {
          label: t('name'),
          enableColumnReordering: false,
        },
      },
      {
        id: 'supplier',
        accessorFn: (row) => row.supplier,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('supplier')} />
        ),
        cell: ({ row }) => (
          <SupplierHoverCard
            className='max-w-[160px] truncate'
            supplier={row.getValue<Supplier>('supplier')}
          />
        ),
        enableColumnFilter: true,
        meta: {
          label: t('supplierId'),
          enableColumnReordering: false,
          variant: 'multiSelect',
          options: supplierOptions,
        },
      },
      {
        id: 'status',
        accessorFn: (row) => row.statusCode,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('status')} />
        ),
        cell: ({ row }) => {
          const status = row.getValue<PurchaseOrderStatusCode>('status');
          return status ? (
            <PurchaseOrderStatusBadge statusCode={status} />
          ) : (
            <div className='text-muted-foreground'>-</div>
          );
        },
        meta: {
          label: t('status'),
          enableColumnReordering: false,
        },
      },
      {
        id: 'declaration',
        accessorFn: (row) => row.productionDeclarationStatus,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('declaration')} />
        ),
        cell: ({ row }) => {
          const status = row.getValue<ProductionDeclarationStatusCode | null>(
            'declaration',
          );
          const purchaseOrderStatus = row.original.statusCode;
          const shouldHideDeclarationStatus =
            purchaseOrderStatus === 'ORDER_CREATION' ||
            purchaseOrderStatus === 'WAITING_FOR_SUPPLIER_AGREEMENT';

          return status && !shouldHideDeclarationStatus ? (
            <PurchaseOrderMainProductionDeclarationStatusBadge
              status={status}
            />
          ) : (
            <div className='text-muted-foreground'>-</div>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('declaration'),
          enableColumnReordering: false,
          variant: 'multiSelect',
          options: declarationOptions,
        },
      },
      {
        id: 'lotSizingId',
        accessorFn: (row) => row.lotSizing,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('lotSizing')} />
        ),
        cell: ({ row }) => {
          const lotSizing = row.getValue<Lotsizing>('lotSizingId');
          return lotSizing ? (
            <Link
              className='text-primary underline hover:text-primary/80'
              onClick={(event) => event.stopPropagation()}
              preload='intent'
              to={`/lot-sizing/${lotSizing.id}`}
            >
              {lotSizing.name}
            </Link>
          ) : (
            <div className='text-muted-foreground'>-</div>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('lotSizing'),
          enableColumnReordering: false,
          variant: 'text',
          placeholder: t('lotSizingIdPlaceholder'),
          filterOperators: [{ label: t('is', { ns: 'table' }), value: 'eq' }],
        },
      },
      {
        id: 'createdAt',
        accessorFn: (row) =>
          (row.createdAt && formatDate(row.createdAt, { locale })) || null,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('createdAt')} />
        ),
        cell: ({ row }) => <div>{row.renderValue('createdAt')}</div>,
        enableColumnFilter: true,
        enableHiding: false,
        meta: {
          label: t('createdAt'),
          enableColumnReordering: false,
          variant: 'date',
        },
      },
    ],
    [t, locale, supplierOptions, declarationOptions],
  ) satisfies ColumnDef<PurchaseOrder>[];
}
